import { Add } from "@mui/icons-material";
import { Box, Button, Stack, Typography } from "@mui/joy";
import { useFieldArray, useFormContext } from "react-hook-form";

import SelectInput from "../forms/SelectInput";
import { NumberInput } from "../forms/TextInput";
import { BONUS_TRIGGERS, bonusToFormValues } from "./bonusForms";
import QuestPartCard from "./QuestPartCard";
import { QuestRulesFormValues } from "./rulesForms";

export default function QuestBonuses() {
  const { control, watch } = useFormContext<QuestRulesFormValues>();
  const { fields, append, remove } = useFieldArray({ control, name: "bonuses" });
  const bonuses = watch("bonuses");

  const unusedTriggers = BONUS_TRIGGERS.filter(
    (option) => !bonuses.some((bonus) => bonus.trigger === option.value),
  );

  return (
    <Stack gap={1.5}>
      <Box>
        <Typography level="h4">Side quests</Typography>
        <Typography level="body-sm" color="neutral">
          Climbers gain bonus credits for performing these actions during the quest. For actions
          that can be performed easily, we recommend setting a limit.
        </Typography>
      </Box>
      {fields.map((field, index) => (
        <QuestPartCard
          key={field.id}
          removeLabel={`Remove side quest ${index + 1}`}
          onRemove={() => remove(index)}
        >
          <BonusFields
            index={index}
            // A quest has one side quest per trigger, but a card can keep its own.
            triggerOptions={BONUS_TRIGGERS.filter(
              (option) =>
                option.value === bonuses[index]?.trigger ||
                unusedTriggers.some((unused) => unused.value === option.value),
            )}
          />
        </QuestPartCard>
      ))}
      <Box>
        <Button
          variant="outlined"
          size="sm"
          startDecorator={<Add />}
          disabled={unusedTriggers.length === 0}
          onClick={() =>
            append(
              bonusToFormValues(
                undefined,
                unusedTriggers.map((option) => option.value),
              ),
            )
          }
        >
          Add side quest
        </Button>
        {unusedTriggers.length === 0 ? (
          <Typography level="body-sm" color="neutral" sx={{ marginTop: 0.5 }}>
            Every kind of side quest has been added.
          </Typography>
        ) : null}
      </Box>
    </Stack>
  );
}

const BonusFields = ({
  index,
  triggerOptions,
}: {
  index: number;
  triggerOptions: typeof BONUS_TRIGGERS;
}) => {
  const { watch } = useFormContext<QuestRulesFormValues>();
  const uuid = watch(`bonuses.${index}.uuid`);

  return (
    <Stack direction="row" gap={1.5} alignItems="flex-start">
      <Box sx={{ flex: 2, minWidth: 0 }}>
        <SelectInput
          name={`bonuses.${index}.trigger`}
          label="Action"
          options={triggerOptions}
          // A saved side quest's action can't change.
          disabled={!!uuid}
        />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <NumberInput name={`bonuses.${index}.creditReward`} label="Credits each time" min={1} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <NumberInput
          name={`bonuses.${index}.maxAwardsPerUser`}
          label="Limit"
          placeholder="Unlimited"
          min={1}
        />
      </Box>
    </Stack>
  );
};
